import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useSession, useWriteGuard } from "@/App";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ResponsiveBreadcrumb } from "@/components/breadcrumb";
import { User, Settings, Lock, Save, Bell } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";

// Form validation schemas
const profileSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  job_title: z.string().optional(),
});

const passwordSchema = z.object({
  current_password: z.string().min(1, "Current password is required"),
  new_password: z.string().min(6, "Password must be at least 6 characters"),
  confirm_password: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

interface UserProfile {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  job_title?: string | null;
  role: string;
  organization_id?: number;
  home_id?: number;
  is_superadmin: boolean;
  email_notifications_enabled?: boolean;
  notification_preferences?: {
    audit_assignment?: boolean;
    audit_completion?: boolean;
    severe_case?: boolean;
    overdue?: boolean;
  };
}

export function SettingsPage() {
  const { toast } = useToast();
  const { session } = useSession();
  const { canWrite, isReadOnly, showWriteBlockedMessage } = useWriteGuard();

  // Fetch current user profile
  const { data: userProfile, isLoading: profileLoading, refetch: refetchProfile } = useQuery<UserProfile>({
    queryKey: ['/api/me'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/me");
      return response.json();
    },
  });

  // Profile form
  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    values: {
      first_name: userProfile?.first_name || "",
      last_name: userProfile?.last_name || "",
      job_title: userProfile?.job_title || "",
    },
  });

  // Password form
  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const response = await apiRequest("PATCH", "/api/me", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update profile");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
      refetchProfile();
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    },
  });

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: PasswordFormData) => {
      const response = await apiRequest("POST", "/api/me/password", {
        current_password: data.current_password,
        new_password: data.new_password,
      });
      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'INVALID_CURRENT_PASSWORD') {
          throw new Error("Your current password is incorrect. Please check and try again.");
        }
        throw new Error(error.message || "Failed to change password");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Password changed",
        description: "Your password has been changed successfully.",
      });
      passwordForm.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Password change failed",
        description: error.message || "Failed to change password",
        variant: "destructive",
      });
    },
  });

  const onProfileSubmit = (data: ProfileFormData) => {
    if (!canWrite) {
      toast(showWriteBlockedMessage());
      return;
    }
    updateProfileMutation.mutate(data);
  };

  const onPasswordSubmit = (data: PasswordFormData) => {
    if (!canWrite) {
      toast(showWriteBlockedMessage());
      return;
    }
    changePasswordMutation.mutate(data);
  };

  // Granular notification toggle mutation
  const updateNotificationsMutation = useMutation({
    mutationFn: async (prefs: Partial<UserProfile['notification_preferences']>) => {
      const response = await apiRequest("PATCH", "/api/me", { notification_preferences: prefs });
      if (!response.ok) {
        throw new Error("Failed to update notification settings");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Settings updated",
        description: "Notification preferences have been saved",
      });
      refetchProfile();
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Failed to update notification settings",
        variant: "destructive",
      });
    },
  });

  const handleToggleNotification = (type: keyof UserProfile['notification_preferences'], enabled: boolean) => {
    if (!canWrite) {
      toast(showWriteBlockedMessage());
      return;
    }
    updateNotificationsMutation.mutate({
      ...userProfile?.notification_preferences,
      [type]: enabled
    });
  };

  if (profileLoading) {
    return (
      <div className="space-y-6">
        <ResponsiveBreadcrumb items={[{ label: "Settings" }]} className="mb-4" />
        <div className="flex h-64 items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-muted-foreground">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto p-6">
      <ResponsiveBreadcrumb items={[{ label: "Settings" }]} className="mb-4" />
      
      <div className="flex items-center space-x-2">
        <Settings className="w-6 h-6 text-primary" />
  <h1 className="page-title text-3xl font-bold text-foreground" data-testid="settings-title">
          Settings
        </h1>
      </div>
      
      <p className="text-muted-foreground">
        Manage your profile information and account settings.
      </p>

      {isReadOnly && (
        <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-950/20" data-testid="readonly-alert">
          <AlertDescription className="text-orange-800 dark:text-orange-200">
            Settings are read-only while viewing as another user.
          </AlertDescription>
        </Alert>
      )}

      {/* Profile Card */}
      <Card data-testid="profile-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="w-5 h-5" />
            <span>Profile</span>
          </CardTitle>
          <CardDescription>
            Update your personal information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...profileForm}>
            <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
              <FormField
                control={profileForm.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        disabled={isReadOnly}
                        data-testid="input-first-name"
                        placeholder="Enter your first name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={profileForm.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        disabled={isReadOnly}
                        data-testid="input-last-name"
                        placeholder="Enter your last name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {/* Hide job_title for self-editing users; job_title is managed by admins/managers for other users */}
              {userProfile && session?.user && userProfile.id !== session.user.id && (
                <FormField
                  control={profileForm.control}
                  name="job_title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Title</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          disabled={isReadOnly}
                          data-testid="input-job-title"
                          placeholder="Enter your job title (optional)"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="flex justify-end pt-4">
                <Button 
                  type="submit" 
                  disabled={isReadOnly || updateProfileMutation.isPending}
                  data-testid="button-save-profile"
                >
                  {updateProfileMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Profile
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card data-testid="password-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Lock className="w-5 h-5" />
            <span>Change Password</span>
          </CardTitle>
          <CardDescription>
            Update your account password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
              <FormField
                control={passwordForm.control}
                name="current_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password" 
                        disabled={isReadOnly}
                        data-testid="input-current-password"
                        placeholder="Enter your current password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="new_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password" 
                        disabled={isReadOnly}
                        data-testid="input-new-password"
                        placeholder="Enter your new password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="confirm_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password" 
                        disabled={isReadOnly}
                        data-testid="input-confirm-password"
                        placeholder="Confirm your new password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end pt-4">
                <Button 
                  type="submit" 
                  disabled={isReadOnly || changePasswordMutation.isPending}
                  data-testid="button-change-password"
                >
                  {changePasswordMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Notifications Card */}
      <Card data-testid="notifications-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bell className="w-5 h-5" />
            <span>Notification Preferences</span>
          </CardTitle>
          <CardDescription>
            Manage which notifications you receive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { key: 'audit_assigned', label: 'Audit Assignment' },
              { key: 'audit_submitted', label: 'Audit Completion' },
              { key: 'audit_for_review', label: 'Audit For Review' },
              { key: 'audit_approved', label: 'Audit Approved' },
              { key: 'audit_reopened', label: 'Audit Reopened' },
              { key: 'overdue_audit', label: 'Overdue Audit' },
              { key: 'severe_case', label: 'Severe Case' }
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="font-medium">{label}</div>
                  <div className="text-sm text-muted-foreground">
                    {`Receive notifications for ${label.toLowerCase()}`}
                  </div>
                </div>
                <Switch
                  checked={userProfile?.notification_preferences?.[key as keyof UserProfile['notification_preferences']] ?? true}
                  onCheckedChange={(enabled) => handleToggleNotification(key as keyof UserProfile['notification_preferences'], enabled)}
                  disabled={isReadOnly || updateNotificationsMutation.isPending}
                  data-testid={`switch-notification-${key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}